'use client'

import { Container } from '@/components/container'
import { SectionHeader } from '@/components/section-header'
import { Button } from '@/components/button'
import { MotionDiv } from '@/components/motion-wrapper'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useEffect, useRef } from 'react'
import { trackEvent } from '@/lib/utils'

const pricingModels = [
  {
    name: 'Fixed Fee',
    description: 'Ideal for well-defined projects with clear scope and deliverables.',
    features: [
      'Fixed project cost',
      'Clear deliverables and timeline',
      'Predictable budgeting',
      'Best for MVPs and specific features',
    ],
    bestFor: 'MVPs, Feature Development, Well-defined Projects',
  },
  {
    name: 'Hourly Rate',
    description: 'Flexible engagement for ongoing work, support, or variable scope projects.',
    features: [
      'Pay for actual time worked',
      'Flexible scope and priorities',
      'Ongoing support and maintenance',
      'Best for consulting and augmentation',
    ],
    bestFor: 'Consulting, Team Augmentation, Ongoing Support',
  },
  {
    name: 'Retainer',
    description: 'Monthly retainer for dedicated resources and priority support.',
    features: [
      'Dedicated monthly hours',
      'Priority support and response',
      'Predictable monthly cost',
      'Best for long-term partnerships',
    ],
    bestFor: 'Long-term Partnerships, Ongoing Development, Priority Support',
  },
]

export function PricingSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const hasTrackedView = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedView.current) {
            hasTrackedView.current = true
            trackEvent('section_viewed', {
              event_category: 'engagement',
              event_label: 'pricing_section',
              value: 1,
            })
          }
        })
      },
      { threshold: 0.5 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  return (
    <section ref={sectionRef} id="pricing" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <Container>
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionHeader
            subheading="Pricing"
            heading="Flexible Engagement Models"
            description="We offer flexible pricing models to suit different project needs and budgets. Contact us for a customized quote."
          />
        </MotionDiv>

        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-12" />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingModels.map((model, index) => (
            <MotionDiv
              key={model.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-xl border border-gray-200 bg-white p-8 shadow-sm hover:shadow-lg transition-shadow h-full flex flex-col">
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  {model.name}
                </h3>
                <p className="text-gray-600 mb-6">
                  {model.description}
                </p>
                
                <ul className="space-y-3 mb-6 flex-grow">
                  {model.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-6 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Best For:</p>
                  <p className="text-sm text-gray-600 mb-4">{model.bestFor}</p>
                  <Button
                    variant="outline"
                    className="w-full"
                    data-cal-namespace="15min"
                    data-cal-link="mohammed-saket-d4virq/15min"
                    data-cal-config='{"layout":"month_view"}'
                    onClick={() => {
                      trackEvent('pricing_contact_clicked', {
                        event_category: 'engagement',
                        event_label: model.name,
                        value: 1,
                      })
                    }}
                  >
                    Contact for Quote
                  </Button>
                </div>
              </div>
            </MotionDiv>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            All pricing is customized based on project scope, timeline, and requirements.
          </p>
          <Button
            data-cal-namespace="15min"
            data-cal-link="mohammed-saket-d4virq/15min"
            data-cal-config='{"layout":"month_view"}'
            onClick={() => {
              trackEvent('pricing_cta_clicked', {
                event_category: 'engagement',
                event_label: 'pricing_section',
                value: 1,
              })
            }}
          >
            Schedule a Free Consultation
          </Button>
        </div>
      </Container>
    </section>
  )
}

